import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Building2, Loader2, Save, ArrowRightLeft, Info, Calculator } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

const MESI = [
  { value: "1", label: "Gennaio" },
  { value: "2", label: "Febbraio" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Aprile" },
  { value: "5", label: "Maggio" },
  { value: "6", label: "Giugno" },
  { value: "7", label: "Luglio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" }
];

export default function RipartizioneCostiPage() {
  const { t } = useTranslation();
  const MESI = [
    { value: '01', label: t('months.january') },
    { value: '02', label: t('months.february') },
    { value: '03', label: t('months.march') },
    { value: '04', label: t('months.april') },
    { value: '05', label: t('months.may') },
    { value: '06', label: t('months.june') },
    { value: '07', label: t('months.july') },
    { value: '08', label: t('months.august') },
    { value: '09', label: t('months.september') },
    { value: '10', label: t('months.october') },
    { value: '11', label: t('months.november') },
    { value: '12', label: t('months.december') },
  ];
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [centriCosto, setCentriCosto] = useState([]);
  const [centriDaRipartire, setCentriDaRipartire] = useState([]);
  const [centriDestinatari, setCentriDestinatari] = useState([]);
  
  // Ripartizioni CdC da ripartire: { centroId: { centroCostoDestId: percentuale } }
  const [ripartizioniCdc, setRipartizioniCdc] = useState({});
  
  const [anno, setAnno] = useState(String(currentYear));
  const [mese, setMese] = useState(String(currentMonth));

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ccRes, ripartRes] = await Promise.all([
        api.get(`${getApiPrefix()}/cost-centers`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/ripartizioni?anno=${anno}&mese=${mese}`).catch(() => ({ data: { appartamenti: {}, centri_costo: {} } }))
      ]);
      
      const cc = ccRes.data || [];
      setCentriCosto(cc);
      setCentriDaRipartire(cc.filter(c => c.da_ripartire));
      setCentriDestinatari(cc.filter(c => !c.da_ripartire));
      
      // Carica ripartizioni esistenti
      if (ripartRes.data) {
        setRipartizioniCdc(ripartRes.data.centri_costo || {});
      }
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [anno, mese, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handler per modificare percentuale centro da ripartire
  const handleCdcPercentChange = (centroOrigineId, centroDestId, value) => {
    const numValue = parseFloat(value) || 0;
    setRipartizioniCdc(prev => ({
      ...prev,
      [centroOrigineId]: {
        ...prev[centroOrigineId],
        [centroDestId]: numValue
      }
    }));
  };

  // Calcola totale percentuali per un centro da ripartire
  const getTotalePercentCdc = (centroId) => {
    if (!ripartizioniCdc[centroId]) return 0;
    return Object.values(ripartizioniCdc[centroId]).reduce((sum, val) => sum + (val || 0), 0);
  };

  // Salva ripartizioni
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`${getApiPrefix()}/ripartizioni`, {
        anno: parseInt(anno),
        mese: parseInt(mese),
        centri_costo: ripartizioniCdc
      });
      showSuccess(t("success.ripartitionsSaved"));
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ripartizione-costi-page">
      <div className="flex items-center gap-3">
        <ArrowRightLeft className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">{t("costAllocation.title")}</h1>
          <p className="text-muted-foreground">Ripartisci i costi dei centri di costo su altri centri</p>
        </div>
      </div>

      {/* Filtri periodo */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label>{t("common.year")}</Label>
              <Select value={anno} onValueChange={setAnno}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('common.month')}</Label>
              <Select value={mese} onValueChange={setMese}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESI.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva Ripartizioni
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Centri di Costo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Ripartizione Centri di Costo
              </CardTitle>
              <CardDescription>
                Definisci come i costi dei centri da ripartire vengono redistribuiti sugli altri centri
              </CardDescription>
            </CardHeader>
            <CardContent>
              {centriDaRipartire.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun centro di costo da ripartire trovato</p>
                  <p className="text-sm">Marca come &quot;da ripartire&quot; i centri di costo che vuoi redistribuire</p>
                </div>
              ) : centriDestinatari.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun centro di costo destinatario trovato</p>
                  <p className="text-sm">Crea dei centri di costo che non siano marcati &quot;da ripartire&quot;</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {centriDaRipartire.map(cdcOrigine => {
                    const totale = getTotalePercentCdc(cdcOrigine.id);
                    const isValid = totale === 0 || totale === 100;
                    return (
                      <div key={cdcOrigine.id} className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              <ArrowRightLeft className="h-4 w-4 text-orange-500" />
                              {cdcOrigine.codice} - {cdcOrigine.nome}
                            </h3>
                            <p className="text-sm text-muted-foreground">{cdcOrigine.descrizione || 'Centro da ripartire'}</p>
                          </div>
                          <div className={`flex items-center gap-2 ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                            <Calculator className="h-4 w-4" />
                            <span className="font-medium">{totale.toFixed(1)}%</span>
                            {!isValid && <span className="text-xs">(deve essere 0% o 100%)</span>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {centriDestinatari.map(ccDest => (
                            <div key={ccDest.id} className="space-y-1">
                              <Label className="text-xs">{ccDest.codice} - {ccDest.nome}</Label>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  className="h-8"
                                  value={ripartizioniCdc[cdcOrigine.id]?.[ccDest.id] || ""}
                                  onChange={(e) => handleCdcPercentChange(cdcOrigine.id, ccDest.id, e.target.value)}
                                  placeholder="0"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
    </div>
  );
}
